import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Check, Loader2, Send } from 'lucide-react';
import type { SetOccurrenceStatusInput, TaskOccurrenceAssignee, TaskStatus } from '@pms/shared';
import { Button } from '@/components/ui/button';
import { tasksService } from '@/services/tasks.service';
import { getErrorMessage } from '@/lib/errors';
import { ASSIGNEE_ACTION } from './taskConstants';

interface AssigneeActionButtonProps {
  row: TaskOccurrenceAssignee;
  onChanged?: () => void;
  disabled?: boolean;
}

/**
 * The one move an assignee can make on their own row: Accept, then Complete.
 *
 * Replaces the status dropdown for assignees. They have no Discard (the creator's call
 * alone) and no way to reach Completed on a reviewable task, so a single button says
 * what is actually available instead of a list that mostly errors.
 *
 * Renders nothing once the ball leaves their court — awaiting review, or finished.
 */
export function AssigneeActionButton({ row, onChanged, disabled }: AssigneeActionButtonProps) {
  const qc = useQueryClient();
  const status = row.status as TaskStatus;
  const action = ASSIGNEE_ACTION[status];

  const setStatus = useMutation({
    mutationFn: (input: SetOccurrenceStatusInput) => tasksService.setStatus(row.id, input),
    onSuccess: (_data, input) => {
      // 'for_review' is what Complete posts; the server turns it into 'completed' for a
      // daily task, so the toast speaks about the submission rather than naming a status.
      toast.success(input.status === 'in_progress' ? 'Task accepted' : 'Task submitted');
      qc.invalidateQueries({ queryKey: ['tasks'] });
      onChanged?.();
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  // Nothing to render while awaiting review or once finished — the status badge shown
  // next to this button already says so, and a second label would just repeat it.
  if (!action) return null;

  const Icon = action.next === 'in_progress' ? Check : Send;

  return (
    <Button
      size="sm"
      variant={action.next === 'in_progress' ? 'outline' : 'default'}
      disabled={disabled || setStatus.isPending}
      onClick={() => setStatus.mutate({ status: action.next })}
    >
      {setStatus.isPending
        ? <Loader2 className="h-4 w-4 animate-spin" />
        : <Icon className="h-4 w-4" />}
      {action.label}
    </Button>
  );
}
